import PublicApp from "./pages/publicRoutes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import loginUserByToken from "./resources/Api/authenticator/loginUserByToken";
import { CookiesProvider, useCookies } from "react-cookie";
import { SelectedParametersType, UserType } from "./types/types";
import { Box, Dialog, Typography } from "@mui/material";
import { userAtom } from "./atoms/atoms";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useLocation, useNavigate } from "react-router-dom";

import PrivateApp from "./pages/privateApp";
import PrivateManagerApp from "./pages/privateManagerApp";
import { setToastRefToOpen } from "./functions/functions";
import { Toast } from "primereact/toast";
import ChangeLogDialog from "./components/ChangeLogDialog";
import useDisableDevTools from './hooks/useDisableDevTools';
import { getTimezone, MANAGER_TIMEZONE } from "./utils/timezone";

const IFRAME_TOKEN_EVENT = "iframe-token-received";

declare global {
  interface Window {
    __tokenListenerRegistered?: boolean;
  }
}

if (!window.__tokenListenerRegistered) {
  window.__tokenListenerRegistered = true;

  window.addEventListener("message", (event: MessageEvent) => {
    if (
      event.data?.action === "token" &&
      typeof event.data.token === "string" &&
      event.data.token.trim()
    ) {
      const token = event.data.token.trim();
      sessionStorage.setItem("token", token);
      window.dispatchEvent(
        new CustomEvent(IFRAME_TOKEN_EVENT, {
          detail: token,
        })
      );
    }
  });
}

const App = () => {
  // atoms
  const getCurrentUser = useRecoilValue<UserType>(userAtom);
  const setCurrentUser = useSetRecoilState<UserType>(userAtom);

  // functionalities states
  //  [isOnline, setIsOnline] = useState(true);
  // const [loading, setLoading] = useState(false);
  const [validationText, setTextValidation] = useState<string>("");
  const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
  const [statusMessage, setStatusMessage] = useState<"warn" | "success" | "info" | "error" | undefined>("warn");
  const [isToastVisible, setIsToastVisible] = useState(false);

  // states
  const [user, setUser] = useState<boolean>(false);
  const [sessionToken, setSessionToken] = useState<string>("");
  const [isUserManager, setIsUserManager] = useState<boolean>(false);

  // refs
  const toastRef = useRef<Toast>(null);

  // cookies
  const [cookies, setCookies, removeCookies] = useCookies(["selectedParameters", "token", "oldToken", "oldSelectedParameters"]);

  const navigate = useNavigate();
  const location = useLocation();
  const authChecked = useRef(false);

  const authToken = useMemo(()  => cookies.token || sessionToken, [cookies.token, sessionToken]);

  const getIframeToken = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const recoveryToken = params.get("token");

    // Login normal ou recuperação não usam o token do iframe.
    if (cookies.token || recoveryToken) {
      return;
    }

    const sessionGotToken = sessionStorage.getItem("token");

    if (sessionGotToken) {
      setSessionToken(sessionGotToken);
    }
  }, [cookies.token, location.search]);

  useDisableDevTools();

  useEffect(() => {
    const handleIframeTokenReceived = () => {
      getIframeToken();
    };

    // Primeiro começa a ouvir novos tokens.
    window.addEventListener(
      IFRAME_TOKEN_EVENT,
      handleIframeTokenReceived
    );

    // Depois verifica se o token chegou antes do App montar.
    getIframeToken();

    return () => {
      window.removeEventListener(
        IFRAME_TOKEN_EVENT,
        handleIframeTokenReceived
      );
    };
  }, [getIframeToken]);

  useEffect(() => {
    const ref = toastRef;
    if (validationText && !isFieldsValid) {
      setToastRefToOpen(
        ref,
        validationText,
        "Atenção!",
        statusMessage,
        undefined,
        3000,
        false
      );
    }
  }, [
    validationText,
    isFieldsValid,
    statusMessage,
  ]);

  const onAuthStateChange = useCallback(async (token: string) => {
      try {
        // data refers to user
        if (token) {
          authChecked.current = true;
        } else {
          authChecked.current = false;
        }

        const { data } = await loginUserByToken(token);
        const currentParameters: SelectedParametersType = cookies.selectedParameters;
        if (
          currentParameters && data.id !== currentParameters.userId) {
          removeCookies("selectedParameters");
        }

        const isManager = Boolean(data.isManager || data.manager);
        const customerSettings = data.customerParameters
          ? { ...data.customerParameters }
          : undefined;
        let authenticatedCustomerUtcZone = MANAGER_TIMEZONE;

        if (!isManager && customerSettings?.useTimezone) {
          const utcZoneFromAuthentication =
            customerSettings.utcZone ??
            data.customerParameters?.timezoneName ??
            data.customerParameters?.timezone?.name ??
            data.customer?.utcZone ??
            customerSettings.timezoneId;

          authenticatedCustomerUtcZone = getTimezone(
            utcZoneFromAuthentication,
          );
        }

        if (customerSettings) {
          customerSettings.utcZone = authenticatedCustomerUtcZone;
        }

        const user: UserType = {
          id: data?.id,
          name: data?.name,
          email: data.email,
          customer: data?.customer ?? null,
          accessProfile: { id: data.accessProfile.id, name: data.accessProfile.name },
          permissions: data.permissions,
          configs: { isManager, isBlocked: data.blocked, isActivated: data.activated, customerSettings, isDriver: data.isDriver || data.driver, useGtf: data?.useGtf, canShareTransparencyPortal: Boolean(data?.canShareTransparencyPortal) }
        };

        setCurrentUser(user);
        setUser(true);
        navigate("/");
      } catch (error: any) {
        const oldToken: string = cookies.oldToken;
        const oldParameters: SelectedParametersType = cookies.oldSelectedParameters;

        if (oldToken) {
          setCookies("token", oldToken);
          setCookies("selectedParameters", oldParameters);

          removeCookies("oldToken");
          removeCookies("oldSelectedParameters");
          localStorage.removeItem("impersonating");
        } else {
          // removeCookies("token");
          // removeCookies("selectedParameters");
        }
    }
  }, [navigate, setCurrentUser, removeCookies, cookies.selectedParameters, setCookies, cookies.oldToken, cookies.oldSelectedParameters]);

  const onLoginVerificationModuleAccess = useCallback(async () => {
    const currentUserManager = getCurrentUser.configs.isManager;
    if (getCurrentUser.id !== -1 && currentUserManager) {
      setIsUserManager(currentUserManager);
    }
  }, [getCurrentUser.configs.isManager, getCurrentUser.id]);

  useEffect(() => {
    if (!authToken) {
      const params = new URLSearchParams(location.search);
      const recoveryToken = params.get("token");
      setUser(false);
      if (!recoveryToken) {
        navigate("/");
      }
    } else {
      if (authChecked.current) return;
      onAuthStateChange(authToken);
    }
  }, [authToken, onAuthStateChange, location.search, navigate]);

  const onUserVerificationStatus = useCallback(async () => {
    const isBlocked = getCurrentUser.configs.isBlocked;
    if (getCurrentUser.id !== -1 && isBlocked) {
      setTextValidation("O usuário está bloqueado, favor entre em contatao com o suporte.");
      setStatusMessage("warn");
      setFieldsIsValid(false);
    } else {
      onLoginVerificationModuleAccess();
    }
  }, [onLoginVerificationModuleAccess, getCurrentUser.configs.isBlocked, getCurrentUser.id]);

  useEffect(() => {
    onUserVerificationStatus();
  }, [onUserVerificationStatus]);

  return (
    <Box className="App">
      {user ? (
        <CookiesProvider>
          <Toast
            ref={toastRef}
            position="center"
            onShow={() => {
              setIsToastVisible(true);

              if (validationText) {
                setTimeout(() => {
                  setFieldsIsValid(true);
                  setIsToastVisible(false);
                }, 3000);
              }
            }}
          />
          <ChangeLogDialog />
          {!isUserManager ? <PrivateApp /> : <PrivateManagerApp />}
        </CookiesProvider>
      ) : (
        <PublicApp />
      )}
    </Box>
  );
};

export default App;
